import { useCallback, useEffect, useState } from 'react';
import { api, errorText, formatDateTime } from '../api';
import { Badge, Card, CheckField, Empty, Loading, Notice, TextField } from '../components/ui';

interface Einstellungen {
  host: string | null;
  port: number;
  secure: boolean;
  benutzer: string | null;
  passwortGesetzt: boolean;
  von_name: string | null;
  von_adresse: string | null;
  antwort_an: string | null;
  aktiv: boolean;
  testmodus: boolean;
}

interface Protokoll {
  id: string;
  an: string;
  betreff: string;
  anlass: string;
  anhaenge: number;
  status: 'gesendet' | 'testmodus' | 'fehler';
  fehler: string | null;
  gesendet_am: string;
  gesendet_von_name: string | null;
}

const STATUS: Record<Protokoll['status'], { kind: 'ok' | 'info' | 'bad'; text: string }> = {
  gesendet: { kind: 'ok', text: 'gesendet' },
  testmodus: { kind: 'info', text: 'Testmodus' },
  fehler: { kind: 'bad', text: 'Fehler' },
};

export function MailPage() {
  const [einstellungen, setEinstellungen] = useState<Einstellungen | null>(null);
  const [protokoll, setProtokoll] = useState<Protokoll[]>([]);
  const [passwort, setPasswort] = useState('');
  const [testAn, setTestAn] = useState('');

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const [daten, log] = await Promise.all([
        api<Einstellungen>('/api/mail/settings'),
        api<Protokoll[]>('/api/mail/log'),
      ]);
      setEinstellungen(daten);
      setProtokoll(log);
    } catch (err) {
      setError(errorText(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function patch(aenderung: Partial<Einstellungen>) {
    setEinstellungen((aktuell) => (aktuell ? { ...aktuell, ...aenderung } : aktuell));
  }

  async function speichern() {
    if (!einstellungen) return;
    setBusy(true);
    setError('');
    try {
      await api('/api/mail/settings', {
        method: 'PUT',
        body: {
          host: einstellungen.host,
          port: einstellungen.port,
          secure: einstellungen.secure,
          benutzer: einstellungen.benutzer,
          // Nur mitschicken, wenn etwas eingegeben wurde — sonst bleibt das
          // hinterlegte Passwort unangetastet.
          ...(passwort ? { passwort } : {}),
          vonName: einstellungen.von_name,
          vonAdresse: einstellungen.von_adresse,
          antwortAn: einstellungen.antwort_an,
          aktiv: einstellungen.aktiv,
          testmodus: einstellungen.testmodus,
        },
      });
      setPasswort('');
      setMessage('Einstellungen gespeichert.');
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function testen() {
    setBusy(true);
    setError('');
    try {
      const bericht = await api<{ status: string; meldung: string }>('/api/mail/test', {
        method: 'POST',
        body: { an: testAn },
      });
      setMessage(bericht.meldung);
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!einstellungen) return error ? <Notice kind="error">{error}</Notice> : <Loading />;

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      {einstellungen.aktiv && einstellungen.testmodus && (
        <Notice kind="info">
          <strong>Testmodus läuft.</strong> Nachrichten werden vollständig erzeugt und
          protokolliert, aber nicht versendet. So lässt sich prüfen, was ankäme, ohne dass es
          ankommt.
        </Notice>
      )}
      {!einstellungen.aktiv && (
        <Notice kind="info">
          Der Versand ist ausgeschaltet. Solange das so bleibt, geht nichts hinaus — auch nicht aus
          Versehen.
        </Notice>
      )}

      <Card title="Postfach">
        <div className="small muted" style={{ marginBottom: 12 }}>
          Die Zugangsdaten des Postfachs, aus dem Rechnungen und Lieferscheine hinausgehen. Das
          Passwort wird verschlüsselt abgelegt und nie wieder angezeigt — wer die Datenbanksicherung
          in die Hände bekommt, hat damit noch nicht das Postfach.
        </div>

        <div className="form-grid">
          <TextField
            label="Server"
            value={einstellungen.host ?? ''}
            onChange={(v) => patch({ host: v })}
            placeholder="mail.ihr-anbieter.de"
          />
          <TextField
            label="Port"
            value={String(einstellungen.port)}
            onChange={(v) => patch({ port: Number(v) || 587 })}
            hint="587 mit STARTTLS, 465 durchgehend verschlüsselt"
          />
          <TextField
            label="Benutzer"
            value={einstellungen.benutzer ?? ''}
            onChange={(v) => patch({ benutzer: v })}
          />
          <TextField
            label={einstellungen.passwortGesetzt ? 'Passwort ändern' : 'Passwort'}
            type="password"
            value={passwort}
            onChange={setPasswort}
            hint={
              einstellungen.passwortGesetzt
                ? 'Ein Passwort ist hinterlegt. Leer lassen heisst: unverändert - ändern sich Server, Port oder Benutzer, muss es neu eingegeben werden.'
                : 'Noch keines hinterlegt'
            }
          />
          <TextField
            label="Absendername"
            value={einstellungen.von_name ?? ''}
            onChange={(v) => patch({ von_name: v })}
          />
          <TextField
            label="Absenderadresse"
            value={einstellungen.von_adresse ?? ''}
            onChange={(v) => patch({ von_adresse: v })}
          />
          <TextField
            label="Antwort an (optional)"
            value={einstellungen.antwort_an ?? ''}
            onChange={(v) => patch({ antwort_an: v })}
            hint="wenn Antworten woanders hin sollen"
          />
        </div>

        <div style={{ marginTop: 4 }}>
          <CheckField
            label="Port ist durchgehend verschlüsselt (465)"
            checked={einstellungen.secure}
            onChange={(v) => patch({ secure: v })}
          />
          <CheckField
            label="Versand eingeschaltet"
            checked={einstellungen.aktiv}
            onChange={(v) => patch({ aktiv: v })}
          />
          <CheckField
            label="Testmodus — erzeugen und protokollieren, aber nicht senden"
            checked={einstellungen.testmodus}
            onChange={(v) => patch({ testmodus: v })}
          />
        </div>

        <div className="small muted" style={{ margin: '8px 0 14px' }}>
          Bietet der Server keine Verschlüsselung an, bricht der Versand ab, statt die Zugangsdaten
          im Klartext über die Leitung zu schicken.
        </div>

        <button className="primary" onClick={speichern} disabled={busy}>
          {busy ? 'Speichern …' : 'Einstellungen speichern'}
        </button>
      </Card>

      <Card title="Testnachricht">
        <div className="small muted" style={{ marginBottom: 12 }}>
          Schickt eine kurze Nachricht an eine Adresse Ihrer Wahl. Läuft der Testmodus, wird sie nur
          protokolliert — das ist der schnellste Weg zu prüfen, ob die Einstellungen greifen, bevor
          etwas an einen Kunden geht.
        </div>
        <div className="form-grid">
          <TextField label="An" value={testAn} onChange={setTestAn} placeholder="ich@meine-firma.de" />
        </div>
        <button onClick={testen} disabled={busy || !testAn.includes('@')}>
          Testnachricht senden
        </button>
      </Card>

      <Card title="Versandprotokoll" padded={false}>
        <div className="body small muted" style={{ paddingBottom: 0 }}>
          Jeder Versuch steht hier, auch der gescheiterte. Die Frage „hat der Kunde die Rechnung
          bekommen" muss sich beantworten lassen.
        </div>
        {protokoll.length === 0 ? (
          <Empty>Noch nichts versendet.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Zeitpunkt</th>
                <th>An</th>
                <th>Betreff</th>
                <th>Anlass</th>
                <th className="num">Anhänge</th>
                <th>Status</th>
                <th>Durch</th>
              </tr>
            </thead>
            <tbody>
              {protokoll.map((eintrag) => (
                <tr key={eintrag.id}>
                  <td className="small muted">{formatDateTime(eintrag.gesendet_am)}</td>
                  <td className="small mono">{eintrag.an}</td>
                  <td className="small">{eintrag.betreff}</td>
                  <td className="small">{eintrag.anlass}</td>
                  <td className="num">{eintrag.anhaenge}</td>
                  <td>
                    <Badge kind={STATUS[eintrag.status].kind}>{STATUS[eintrag.status].text}</Badge>
                    {eintrag.fehler && (
                      <div className="small" style={{ color: 'var(--bad)', marginTop: 4 }}>
                        {eintrag.fehler}
                      </div>
                    )}
                  </td>
                  <td className="small">{eintrag.gesendet_von_name ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}
